import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Sparkles, LayoutDashboard, Users, Activity, FileText, LogOut, Globe,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import PortalNotificationBell from "@/components/PortalNotificationBell";

/**
 * Phase 5A — Portal layout for invited Principal viewers.
 * Read-only sandbox auth-isolated from the main admin app.
 */
export default function PortalLayout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [session, setSession] = useState(undefined); // undefined=loading, null=anonymous

  const fetchMe = async () => {
    try {
      const { data } = await api.get("/portal/me");
      setSession(data);
    } catch {
      setSession(null);
    }
  };

  useEffect(() => { fetchMe(); /* eslint-disable-next-line */ }, []);

  const logout = async () => {
    try { await api.post("/principal-viewer/logout"); } catch (e) { /* ignore */ }
    setSession(null);
    navigate("/portal/login");
  };

  if (session === undefined) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-muted-foreground">
        <div className="text-sm">{t("portal.loading")}</div>
      </div>
    );
  }
  if (session === null) return <Navigate to="/portal/login" replace />;

  const company = session.company || {};

  const navItems = [
    { to: "/portal/home", icon: LayoutDashboard, label: t("portal.nav.cockpit") },
    { to: "/portal/prospects", icon: Users, label: t("portal.nav.prospects") },
    { to: "/portal/activities", icon: Activity, label: t("portal.nav.activities") },
    { to: "/portal/digests", icon: FileText, label: t("portal.nav.digests") },
  ];

  return (
    <div className="min-h-screen bg-background flex" data-testid="portal-layout">
      <aside className="w-60 border-r bg-card flex flex-col">
        <div className="px-5 py-5 border-b">
          <div className="flex items-center gap-2 text-emerald-600">
            <Sparkles className="h-4 w-4" />
            <span className="text-[10px] font-semibold tracking-[0.14em] uppercase">
              {t("portal.brand")}
            </span>
          </div>
          <div className="mt-2 text-base font-semibold truncate" title={company.name}>
            {company.name}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {t("portal.welcome", { name: session.viewer?.name || session.viewer?.email })}
          </div>
        </div>

        <nav className="flex-1 px-2 py-3 space-y-1">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-700 font-medium"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
              data-testid={`portal-nav-${it.to.split("/").pop()}`}
            >
              <it.icon className="h-3.5 w-3.5" />
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t p-3 space-y-1">
          <PortalNotificationBell />
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs"
            onClick={() => i18n.changeLanguage(i18n.language === "fr" ? "en" : "fr")}
            data-testid="portal-lang-toggle"
          >
            <Globe className="h-3 w-3 mr-2" /> {i18n.language === "fr" ? "EN" : "FR"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs text-red-600 hover:bg-red-500/10"
            onClick={logout}
            data-testid="portal-logout-btn"
          >
            <LogOut className="h-3 w-3 mr-2" /> {t("portal.logout")}
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <Outlet context={{ session, refresh: fetchMe }} />
        </div>
      </main>
    </div>
  );
}
